import React, { useEffect, useMemo, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaChevronDown,
  FaFilter,
  FaSortAmountDown,
  FaTag,
  FaLayerGroup,
  FaSearch,
  FaRupeeSign,
  FaImage,
  FaPlay,
} from "react-icons/fa";
import "./Modules.css";
import { BASE_URL } from "../../Config/config";
export default function Modules() {
  /* =====================================================
     BASE URL
  ===================================================== */

  /* =====================================================
     STATES
  ===================================================== */

  const [modules, setModules] = useState([]);

  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  /* =====================================================
     FETCH MODULES
  ===================================================== */

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${BASE_URL}/modules.php?action=get`);

      const text = await response.text();

      console.log("MODULE API RESPONSE:", text);

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Invalid module API response:", text);

        setError("Invalid response received from server.");
        setModules([]);

        return;
      }

      if (result.status) {
        setModules(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch modules.");

        setError(result.message || "Failed to fetch modules.");

        setModules([]);
      }
    } catch (error) {
      console.error("Fetch modules error:", error);

      setError("Unable to load modules. Please try again later.");

      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     LOAD MODULES
  ===================================================== */

  useEffect(() => {
    fetchModules();
  }, []);

  /* =====================================================
     IMAGE URL
  ===================================================== */

  const getModuleImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     DYNAMIC CATEGORIES
  ===================================================== */

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(
        modules
          .map((module) => module.category)
          .filter((category) => category && String(category).trim()),
      ),
    ];

    return uniqueCategories.sort((a, b) => String(a).localeCompare(String(b)));
  }, [modules]);

  /* =====================================================
     FILTER + SEARCH + SORT
  ===================================================== */

  const filteredModules = useMemo(() => {
    const searchValue = search.toLowerCase().trim();

    return [...modules]
      .filter((module) => {
        /* CATEGORY */

        const matchesCategory =
          !category || String(module.category || "") === category;

        /* SEARCH */

        const matchesSearch =
          !searchValue ||
          String(module.title || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(module.code || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(module.description || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(module.category || "")
            .toLowerCase()
            .includes(searchValue);

        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => {
        /* LOW TO HIGH */

        if (sort === "low") {
          return Number(a.price || 0) - Number(b.price || 0);
        }

        /* HIGH TO LOW */

        if (sort === "high") {
          return Number(b.price || 0) - Number(a.price || 0);
        }

        return 0;
      });
  }, [modules, category, search, sort]);
  /* =====================================================
   PAGINATION
===================================================== */

  const totalPages = Math.ceil(filteredModules.length / itemsPerPage);

  const paginatedModules = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredModules.slice(startIndex, endIndex);
  }, [filteredModules, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =====================================================
     RESET FILTERS
  ===================================================== */
  const resetFilters = () => {
    setCategory("");
    setSort("");
    setSearch("");
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [category, search, sort]);
  /* =====================================================
     FORMAT PRICE
  ===================================================== */

  const formatPrice = (price) => {
    const numericPrice = Number(price || 0);

    return numericPrice.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
  }, []);
  useEffect(() => {
    AOS.refresh();
  }, [category, search, sort, currentPage, paginatedModules.length]);
  /* =====================================================
     RETURN
  ===================================================== */

  return (
    <main className="sc-modules-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section
        className="sc-modules-hero"
        data-aos="fade-down"
        data-aos-duration="1000"
      >
        <div className="sc-modules-hero-overlay"></div>

        <div
          className="sc-modules-container sc-modules-hero-content"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <span className="sc-modules-label">TALLY MODULES</span>

          <h1>
            Powerful Tally Modules
            <span>For Your Business</span>
          </h1>

          <p>
            Star Computers partners with finance and operations teams to design
            Tally ERP implementations that are accurate, compliant, and easy to
            use.
          </p>

          {/* =================================================
              HERO STATS
          ================================================= */}

          <div
            className="sc-modules-hero-stats"
            data-aos="fade-up"
            data-aos-delay="350"
          >
            {/* MODULES */}

            <div className="sc-modules-hero-stat">
              <strong>{loading ? "..." : `${modules.length}+`}</strong>

              <span>Modules</span>
            </div>

            {/* CATEGORIES */}

            <div className="sc-modules-hero-stat">
              <strong>{loading ? "..." : `${categories.length}+`}</strong>

              <span>Categories</span>
            </div>

            {/* SUPPORT */}

            <div className="sc-modules-hero-stat">
              <strong>24/7</strong>

              <span>Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          MODULES CONTENT
      ===================================================== */}

      <section className="sc-modules-content">
        <div className="sc-modules-container">
          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div className="sc-modules-section-heading" data-aos="fade-up">
            <div>
              <span className="sc-modules-heading-label">
                TALLY PRIME MODULES
              </span>

              <h2>
                Find the Right
                <span> Module for You</span>
              </h2>

              <p>
                Browse our collection of TallyPrime modules and choose the right
                solution for your business.
              </p>
            </div>

            {/* MODULE COUNT */}

            <div className="sc-modules-count">
              <FaLayerGroup />

              <strong>{loading ? "..." : filteredModules.length}</strong>

              <span>Modules Found</span>
            </div>
          </div>

          {/* =================================================
              FILTER BOX
          ================================================= */}

          <div
            className="sc-modules-filter-box"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            {/* =================================================
                SEARCH
            ================================================= */}

            <div className="sc-modules-filter">
              <label>
                <FaSearch />
                Search Module
              </label>

              <div className="sc-modules-search-input">
                <FaSearch />

                <input
                  type="text"
                  placeholder="Search module..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* =================================================
                CATEGORY
            ================================================= */}

            <div className="sc-modules-filter">
              <label>
                <FaFilter />
                Category
              </label>

              <div className="sc-modules-select">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select category</option>

                  {categories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                <FaChevronDown />
              </div>
            </div>

            {/* =================================================
                SORT
            ================================================= */}

            <div className="sc-modules-filter">
              <label>
                <FaSortAmountDown />
                Sort By
              </label>

              <div className="sc-modules-select">
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="">Sort By</option>

                  <option value="low">Low To High</option>

                  <option value="high">High To Low</option>
                </select>

                <FaChevronDown />
              </div>
            </div>

            {/* =================================================
                RESET
            ================================================= */}

            <div className="sc-modules-filter">
              <label>&nbsp;</label>

              <button
                type="button"
                onClick={resetFilters}
                className="sc-modules-reset"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* =================================================
              ERROR
          ================================================= */}

          {error && (
            <div className="sc-modules-error">
              <FaLayerGroup />

              <h3>Unable to Load Modules</h3>

              <p>{error}</p>

              <button type="button" onClick={fetchModules}>
                Try Again
              </button>
            </div>
          )}

          {/* =================================================
              LOADING
          ================================================= */}

          {loading ? (
            <div className="sc-modules-loading">
              <div className="sc-modules-loading-spinner"></div>

              <p>Loading modules...</p>
            </div>
          ) : !error ? (
            /* =================================================
               MODULE GRID
            ================================================= */

            <div className="sc-modules-grid">
              {filteredModules.length > 0 ? (
                paginatedModules.map((module, index) => (
                  <article
                    className="sc-modules-card"
                    key={module.id || module.code || module.title}
                    data-aos="fade-up"
                    data-aos-delay={100 + (index % 4) * 100}
                    data-aos-duration="700"
                  >
                    {/* =================================================
                        IMAGE
                    ================================================= */}

                    <div className="sc-modules-card-image">
                      {module.image ? (
                        <img
                          src={getModuleImageUrl(module.image)}
                          alt={module.title || "Tally Module"}
                          loading="lazy"
                        />
                      ) : (
                        <div className="sc-modules-no-image">
                          <FaImage />
                          <span>No Image</span>
                        </div>
                      )}

                      {/* YOUTUBE PLAY BUTTON */}
                      {module.youtube && (
                        <a
                          href={module.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sc-modules-youtube-button"
                          aria-label={`Watch ${module.title || "module"} video`}
                          title="Watch YouTube Video"
                        >
                          <FaPlay />
                        </a>
                      )}

                      {/* CODE */}
                      {module.code && (
                        <span className="sc-modules-code">
                          CODE: {module.code}
                        </span>
                      )}
                    </div>

                    {/* =================================================
                        CARD CONTENT
                    ================================================= */}

                    <div className="sc-modules-card-content">
                      {/* TITLE */}

                      <h3>{module.title || "Untitled Module"}</h3>

                      {/* CATEGORY */}

                      {module.category && (
                        <div className="sc-modules-category">
                          <span>
                            <FaTag />

                            {module.category}
                          </span>
                        </div>
                      )}

                      {/* DESCRIPTION */}

                      {module.description && (
                        <p className="sc-modules-description">
                          {module.description}
                        </p>
                      )}

                      {/* PRICE */}

                      <div className="sc-modules-pricing">
                        <div className="sc-modules-main-price">
                          <span>
                            <FaRupeeSign />
                          </span>

                          <strong>{formatPrice(module.price)}</strong>
                        </div>
                      </div>

                      {/* BUY BUTTON */}

                      <a
                        href={`/contact?${new URLSearchParams({
                          product: "Tally Modules",
                          module: module.title || "",
                          title: `Tally Modules - ${module.title || ""}`,
                          code: module.code || "",
                          price: module.price || "Price on request",
                          plan: module.title || "",
                          message: `Hello, I am interested in the Tally Module "${module.title || ""}"${
                            module.code ? ` (Code: ${module.code})` : ""
                          }. The listed price is ${
                            module.price
                              ? `₹${module.price}`
                              : "Price on request"
                          }. Please provide more details and help me with the purchase.`,
                        }).toString()}`}
                        className="sc-modules-buy-button"
                      >
                        Buy Now
                        <FaChevronDown />
                      </a>
                    </div>
                  </article>
                ))
              ) : (
                /* =================================================
                   EMPTY STATE
                ================================================= */

                <div className="sc-modules-empty">
                  <FaLayerGroup />

                  <h3>No Modules Found</h3>

                  <p>Try changing your category or search criteria.</p>

                  <button type="button" onClick={resetFilters}>
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
        {/* =====================================================
    PAGINATION
===================================================== */}

        {filteredModules.length > itemsPerPage && (
          <div className="sc-modules-pagination">
            {/* PREVIOUS */}
            <button
              type="button"
              className="sc-modules-page-button"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Previous
            </button>

            {/* PAGE NUMBERS */}
            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              return (
                <button
                  type="button"
                  key={page}
                  className={`sc-modules-page-button ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              );
            })}

            {/* NEXT */}
            <button
              type="button"
              className="sc-modules-page-button"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
